/**
 * The route tree, assembled in CODE (PRD D2: "route-tree is code, not filesystem
 * convention — no gate rewrite for path layout").
 *
 * That is a real constraint, not a preference: 52 of this repo's 541 gate scripts
 * are keyed to App Router filesystem conventions (PRD §7, R4). A code-defined
 * tree lets the URL map change without any of them having to relearn a directory
 * shape, and lets the ~30 `next.config.ts` redirects and 83 dynamic segments land
 * as ordinary route definitions when RO-3-14 ports them.
 *
 * Three routes today, matching the three the spine serves. RO-0-06 is the
 * prove-out (static / dynamic-segment / mutation) and L3 grows this to the full
 * 238-page map — adding a route here is a one-line change by design.
 */

import { createRouter } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import {
  agentsIndexRoute,
  agentsRetrievalRoute,
  agentsPackRoute,
  agentsProofFtsRoute,
  agentsProofRetrievalRoute,
  agentsProofPackRoute,
} from "./agents";
import { changesSectionRoute, contextSectionRoute } from "./section-roots";
import { agentsRunDetailRoute } from "./agent-run-detail";
import { objectRoute } from "./object-page";
import {
  agentsFleetRoute,
  agentsFleetQualityRoute,
  agentsAnalyticsFleetRoute,
  agentsAnalyticsFleetQualityRoute,
  agentsRunsRoute,
  agentsObjectRoute,
} from "./agent-redirects";
import {
  agentsGoalsRoute,
  agentsGoalsNewRoute,
  agentsGoalRoute,
} from "./agent-goals";
import { agentsGoalDefineRoute } from "./agent-goal-define";
import { agentsQaRoute } from "./agent-qa";
import { agentsQaRunRoute } from "./agent-qa-run";
import {
  agentsVerificationRoute,
  agentsVerificationDetailRoute,
  verificationRoute,
  verificationDetailRoute,
} from "./agent-verification";
import { agentsTaskDetailRoute } from "./agent-task-detail";
import { agentsHealthRoute } from "./agent-health";
import { agentsLanesRoute } from "./agent-lanes";
import {
  agentsSessionsRoute,
  SessionTrajectoryRoute as agentsSessionsTrajectoryRoute,
} from "./agent-sessions";
import { agentsVerificationOpsRoute } from "./agent-verification-ops";
// #46036 — Phase 3.G/3.H operator views (siblings 46034/46035 also edit this file).
import { agentsClaimsRoute } from "./agent-claims";
import { agentsTriageRoute } from "./agent-triage";
import { agentsRoadmapRoute } from "./agent-roadmap";
import {
  agentsAnalyticsRoute,
  agentsAnalyticsYouRoute,
  agentsAnalyticsYouActivityRoute,
  agentsAnalyticsYouCostsRoute,
  agentsAnalyticsYouExportRoute,
  agentsAnalyticsYouHistoryRoute,
  agentsAnalyticsYouMemoryRoute,
  agentsAnalyticsYouPlansRoute,
  agentsAnalyticsYouProjectsRoute,
  agentsAnalyticsYouSessionsRoute,
  agentsAnalyticsYouSettingsRoute,
  agentsAnalyticsYouTodosRoute,
  agentsAnalyticsYouToolsRoute,
  agentsAnalyticsYouIntegrationsRoute,
  agentsAnalyticsYouProjectRoute,
  agentsAnalyticsYouSessionRoute,
} from "./agents-analytics";
import { agentsWorkflowTraceRoute } from "./agent-workflow-trace";
import { agentsSurfaceProofRoute } from "./surface-proof";
import {
  agentsWorkflowsRoute,
  agentsWorkflowsNewRoute,
} from "./agent-workflows";
import { agentsWorkflowRoute } from "./agent-workflow-detail";
import { agentsWorkflowCompiledRoute } from "./agent-workflow-compiled";
import { agentsSharedRenderProofRoute } from "./shared-render-proof";
import { hiringJobRoute } from "./hiring";
import { productIndexRoute, productSectionsRoute } from "./product";
import {
  propertiesIndexRoute,
  propertiesNewRoute,
  propertiesUnattributedRoute,
} from "./properties";
import { customersRoute, customersSectionsRoute } from "./customers";
import { marketRoute } from "./market";
import {
  operationsIndexRoute,
  operationsSectionsRoute,
  directoryEntityRoute,
} from "./operations";
import { welcomeIndexRoute, welcomeStepRoute } from "./welcome";
import { transparencyRoute } from "./transparency";
import {
  styleguideVerificationRoute,
  styleguideVerificationSplatRoute,
} from "./styleguide-verification";
import { skeletonsRoute, joinRoute } from "./misc";
// #44577 — unmounted top-level singletons (home, login, help, …).
import {
  homeRoute,
  loginRoute,
  helpRoute,
  helpSettingsRoute,
  devRoute,
  excellentNowRoute,
  notificationsRoute,
  integrationsConnectedRoute,
  onboardingWorkspaceRoute,
} from "./singletons";
import {
  inboxRoute,
  queueRoute,
  findingsRoute,
  runsRoute,
  workRoute,
  workflowsRoute,
  controlsRoute,
  policiesRoute,
  evidenceRoute,
  connectorsRoute,
  environmentsRoute,
  receiptsRoute,
  packsRoute,
  auditRoomRoute,
  workersRoute,
  scorecardRoute,
  runtimeRoute,
  sdkRoute,
  demoLabRoute,
  trustRoute,
  postureRoute,
  guideRoute,
  buyableRoute,
  designRoute,
} from "./verification-ia-routes";
import {
  outcomesRoute,
  autonomyRoute,
  analyticsRoute,
} from "./verification-product-routes";
import { classicHomeRoute, classicVerificationRoute } from "./classic";
// RO-3-11 — the /database section (16 routes across four files, split by surface).
import {
  databaseIndexRoute,
  databaseSchemaRoute,
  databaseSchemaObjectRoute,
  databaseMigrationsRoute,
  databaseSqlRoute,
  databaseSyncRoute,
  databaseImportRoute,
  databaseAssistantRoute,
} from "./database";
import {
  databaseEditorIndexRoute,
  databaseTableEditorRoute,
} from "./database-editor";
import {
  databaseObjectsRoute,
  databaseObjectsNewRoute,
  databaseObjectTypeRoute,
} from "./database-objects";
import {
  databaseConflictsRoute,
  databaseDuplicatesRoute,
  databaseViewsRoute,
} from "./database-queues";
// RO-3-09 — /settings core (user, appearance, workspace, customers) + redirects.
import {
  settingsIndexRoute,
  settingsUserIndexRoute,
  settingsSectionsRoute,
  settingsReposRoute,
  settingsIntegrationsConnectionsRoute,
  settingsIntegrationsCatalogRoute,
  settingsCustomersIndexRoute,
  settingsAutoVerifyRoute,
  settingsWorkspaceIdentityRoute,
  settingsAgentsGoalsRoute,
  settingsAgentsWorkersRoute,
} from "./settings-redirects";
import {
  settingsPersonalAliasRoute,
  settingsAppsAliasRoute,
  settingsAdvancedAliasRoute,
  settingsHelpAliasRoute,
  settingsDatabaseAliasRoute,
  preferencesAliasRoute,
  profileAliasRoute,
  accountAliasRoute,
  docsAliasRoute,
  documentationAliasRoute,
  helpCenterAliasRoute,
  dbAliasRoute,
  sqlAliasRoute,
} from "./verification-utility-redirects";
import { verificationNotFoundRecoveryRoute } from "./verification-not-found-recovery";
import { verificationIaRoutes } from "./verification-ia-routes";
import {
  settingsUserProfileRoute,
  settingsUserPreferencesRoute,
  settingsUserNotificationsRoute,
  settingsUserKeyboardRoute,
  settingsAppearanceRoute,
} from "./settings-user";
import {
  settingsWorkspaceRoute,
  settingsWorkspaceSecurityRoute,
} from "./settings-workspace";
// Lane APPROVESURFACE — the owner's browser door onto headless device enrollment.
import { settingsWorkspaceDevicesRoute } from "./settings-device-enrollment";
// #44573 — /settings/agents/* (22 routes; largest hole in the rollover).
import {
  settingsAgentsReposRoute,
  settingsAgentsAutoVerifyRoute,
  settingsAgentsAuditsRoute,
  settingsAgentsAutopilotRoute,
  settingsAgentsClisRoute,
  settingsAgentsConsentRoute,
  settingsAgentsContextRoute,
  settingsAgentsCopilotRoute,
  settingsAgentsCredentialsRoute,
  settingsAgentsDatabaseRoute,
  settingsAgentsFleetRoute,
  settingsAgentsInboundRoute,
  settingsAgentsIntegrateRoute,
  settingsAgentsLearningRoute,
  settingsAgentsMirrorRoute,
  settingsAgentsResidencyRoute,
  settingsAgentsMonitorRoute,
  settingsAgentsNotifyRoute,
  settingsAgentsOptimizationRoute,
  settingsAgentsTelemetryRoute,
  settingsAgentsVerdictsRoute,
} from "./settings-agents";
import {
  settingsCustomersPipelinesRoute,
  settingsCustomersLeadsRoute,
  settingsCustomersMessagingRoute,
  settingsCustomersSendingRoute,
  settingsCustomersDataRoute,
} from "./settings-customers";
import { settingsAgentsModelsRoute } from "./settings-agents-models";
// #44574 — remaining settings: advanced, integrations, workspace-more, singletons.
import {
  settingsAdvancedDangerRoute,
  settingsAdvancedDataRoute,
  settingsAdvancedMcpRoute,
  settingsAdvancedPerformanceRoute,
} from "./settings-advanced";
import {
  settingsAdvisorRoute,
  settingsCompanyProfileRoute,
  settingsPulseRoute,
} from "./settings-singletons";
import {
  settingsIntegrationsHomeRoute,
  settingsIntegrationsActivityRoute,
  settingsIntegrationsConflictsRoute,
  settingsIntegrationsDatabaseRoute,
  settingsIntegrationsSettingsRoute,
  settingsIntegrationsSourcesNewRoute,
  settingsIntegrationsMappingRoute,
  settingsIntegrationsRecordsRoute,
} from "./settings-integrations-pages";
import {
  settingsWorkspaceArchiveRoute,
  settingsWorkspaceDataRoute,
  settingsWorkspaceDirectoryRoute,
  settingsWorkspaceDomainsRoute,
  settingsWorkspaceSectionsRoute,
  settingsWorkspaceTeamRoute,
  settingsWorkspaceTeamDevicesRoute,
  settingsWorkspaceTeamMembersRoute,
  settingsWorkspaceTeamPrimaryRoute,
  settingsWorkspaceTeamRolesRoute,
} from "./settings-workspace-more";
import { dynamicSegmentRoutes } from "./dynamic-segments";
// RO-3-03 — the Work section (9 pages: mode root, section catch-all, object
// redirect, issues REWRITE, planner, activity, favorites, suggestions, verdicts).
import {
  workIndexRoute,
  workSectionsRoute,
  workObjectRoute,
  issuesRoute,
  plannerRoute,
  activityRoute,
  favoritesRoute,
  suggestionsRoute,
  overridesRoute,
  verdictsRoute,
} from "./work";
// RO-3-10 / #44303 — projects + section-hosted views.
import {
  projectsIndexRoute,
  projectsMapRoute,
  projectsMapDefineRoute,
  projectHomeRoute,
  projectTasksRedirectRoute,
  projectSettingsIdentityRoute,
  projectSettingsBriefRoute,
  projectSettingsTrackingRoute,
  projectSettingsOrganizationRoute,
  projectSettingsSectionsRoute,
  projectSettingsPreferencesRoute,
  projectSectionRoute,
  projectSectionCatchAllRoute,
  workspaceSectionRoute,
  workspaceSectionDashboardRoute,
  workspaceSectionAuditRoute,
  workspaceSectionIssuesRoute,
  projectLeadsRoute,
  projectLeadsNewRoute,
  projectOpportunitiesRoute,
  projectOpportunitiesNewRoute,
  projectSequencesRoute,
  projectSequencesNewRoute,
  projectTemplatesRoute,
  projectTemplatesNewRoute,
  projectReportsRoute,
  projectSectionSettingsRoute,
  projectJobsRoute,
  projectJobsNewRoute,
  projectJobDetailRoute,
  projectJobEditRoute,
  projectJobPipelineRoute,
  projectCandidatesRoute,
  projectPipelineRoute,
  projectLeadDetailRoute,
  projectOpportunityDetailRoute,
  projectGoalDetailRoute,
  projectSequenceDetailRoute,
  projectApplicationDetailRoute,
  projectCandidateDetailRoute,
  projectSectionOverviewRoute,
  projectSectionAuditRoute,
  projectSectionImportRoute,
  projectSectionDirectoryRoute,
  projectSectionSimulateRoute,
  projectSectionChecklistRoute,
  projectSectionComponentsRoute,
  projectSectionTypographyRoute,
  projectSectionStyleGuideRoute,
  // #44578 — literal leaves that outrank catch-alls / section root
  projectSectionAgentsRoute,
  projectSectionAnalyticsRoute,
  projectSectionIssuesRoute,
  projectSectionPerformanceRoute,
  projectSectionPlannerRoute,
  projectSectionPrdRoute,
  projectSectionSeoRoute,
  projectSectionGoalsNewRoute,
  projectTierAgentsRoute,
  projectTierIssuesRoute,
  projectTierPlannerRoute,
} from "./projects";

export const routeTree = rootRoute.addChildren([
  agentsIndexRoute,
  contextSectionRoute,
  changesSectionRoute,
  agentsRetrievalRoute,
  agentsPackRoute,
  agentsProofFtsRoute,
  agentsProofRetrievalRoute,
  agentsProofPackRoute,
  // Canonical object page + run detail (Agents recovery — closed pending stubs).
  objectRoute,
  agentsRunDetailRoute,
  // RO-3-02 — /agents page port (12 of 37 pages; see the task evidence for the
  // full ported-vs-deferred split).
  agentsFleetRoute,
  agentsFleetQualityRoute,
  agentsAnalyticsFleetRoute,
  agentsAnalyticsFleetQualityRoute,
  agentsRunsRoute,
  agentsObjectRoute,
  agentsGoalsRoute,
  agentsGoalsNewRoute,
  agentsGoalRoute,
  agentsQaRoute,
  // SKU A dogfood — evidence / dual-agent / claims / receipts surfaces.
  // Literal list path BEFORE the `$taskId` sibling.
  verificationRoute,
  verificationDetailRoute,
  agentsVerificationRoute,
  agentsVerificationDetailRoute,
  agentsTaskDetailRoute,
  // RO-4-03 — design-system reachability proof. Scaffold with an expiry date;
  // see the header of `surface-proof.tsx` for when to delete it.
  agentsSurfaceProofRoute,
  // RO-3-02b — the `agents/workflows/*` pages RO-3-02 deferred.
  // RO-3-02c (#44399) closes `workflows/compiled/$slug` + the Gates section.
  // Literal paths BEFORE the dynamic `$slug` sibling: TanStack ranks by
  // specificity rather than registration order, so this is habit rather than
  // load-bearing — but the Hono side is registration-ordered (#44370) and one
  // convention across both halves is cheaper than remembering which is which.
  agentsWorkflowsRoute,
  agentsWorkflowsNewRoute,
  agentsWorkflowCompiledRoute,
  agentsWorkflowRoute,
  // RO-3-13 — `/agents` rewrites. `agents/analytics` closed by #44575.
  // `agents/runs/$id` closed by agent-run-detail.tsx (Agents recovery).
  //
  // `agentsQaRunRoute` closes the 404 `agent-qa.tsx`'s header documents, and
  // `agentsRoadmapRoute` closes the one `agent-workflows.tsx:93` links to.
  agentsGoalDefineRoute,
  agentsQaRunRoute,
  agentsHealthRoute,
  agentsLanesRoute,
  agentsVerificationOpsRoute,
  // #46036 — claims/lanes + triage board
  agentsClaimsRoute,
  agentsTriageRoute,
  agentsRoadmapRoute,
  // SI-G2 — read-only session surface (list + trajectory detail). The literal
  // `/agents/sessions` before its `/$id` sibling.
  agentsSessionsRoute,
  agentsSessionsTrajectoryRoute,
  agentsWorkflowTraceRoute,
  // #44575 — Agents ▸ Analytics hub + You deep-dives (14 paths + 2 dynamics).
  // Literals before `$slug`/`$id` siblings.
  agentsAnalyticsRoute,
  agentsAnalyticsYouRoute,
  agentsAnalyticsYouActivityRoute,
  agentsAnalyticsYouCostsRoute,
  agentsAnalyticsYouExportRoute,
  agentsAnalyticsYouHistoryRoute,
  agentsAnalyticsYouMemoryRoute,
  agentsAnalyticsYouPlansRoute,
  agentsAnalyticsYouProjectsRoute,
  agentsAnalyticsYouSessionsRoute,
  agentsAnalyticsYouSettingsRoute,
  agentsAnalyticsYouTodosRoute,
  agentsAnalyticsYouToolsRoute,
  agentsAnalyticsYouIntegrationsRoute,
  agentsAnalyticsYouProjectRoute,
  agentsAnalyticsYouSessionRoute,
  // RO-3-00 — the shared render layer's reachability proof. Same scaffold
  // discipline as `agentsSurfaceProofRoute` above, and it exists for a measured
  // reason: without a route importing `client/shared/**`, Rollup never walks it
  // and the Vite build's module count does not move, so "the client builds" says
  // nothing about the ported layer. Delete with the section ports (see the
  // route file's header).
  agentsSharedRenderProofRoute,
  // RO-3-08 — the Personal section (both of its pages). The literal
  // `/personal/collections` sits beside `/personal` rather than under it: the
  // original had no shared layout segment between them, so nesting would invent
  // one. Same literal-before-dynamic habit as the workflows block above, though
  // neither of these is dynamic.
  // RO-3-07 — the `hiring` section. ONE route for two pages: the public/operator
  // job page. `hiring/page.tsx` is deliberately NOT registered — shell cascade
  // already redirects `/hiring` → `/operations?area=hiring`.
  hiringJobRoute,
  // RO-3-05 — the `product` section. Mode root + section catch-all; properties
  // index/new/unattributed. `/product/o/$uid` stays in dynamic-segments (RO-3-14
  // pure redirect). Literal property paths before any dynamic sibling.
  productIndexRoute,
  productSectionsRoute,
  propertiesIndexRoute,
  propertiesNewRoute,
  propertiesUnattributedRoute,
  // RO-3-04 — the `customers` section (mode root + section catch-all + /market).
  // Literal `/customers` BEFORE the `$` splat sibling. Object redirect
  // `/customers/o/$uid` stays in dynamic-segments (RO-3-14, already PORTED).
  customersRoute,
  customersSectionsRoute,
  marketRoute,
  // RO-3-06 — the `operations` section. Mode root + section catch-all + the
  // directory id→uid hop. `/finance` `/team` `/directory` `/audits` are
  // shell-cascade folds (no route) — same discipline as hiring's bare page.
  // `/operations/o/$uid` stays in dynamic-segments (RO-3-14 pure redirect).
  // `/integrations/connected` is mounted once by #44577 singletons below.
  operationsIndexRoute,
  operationsSectionsRoute,
  directoryEntityRoute,
  // RO-3-12 / #44576 — `/welcome` hub is a real page (onboarding gate 1 lands
  // here). `/welcome/$step` redirects to the hub. transparency + misc tail
  // unchanged.
  welcomeIndexRoute,
  welcomeStepRoute,
  transparencyRoute,
  skeletonsRoute,
  joinRoute,
  // `/styleguide/verification` — WP1b / #46598 + WP10a / #46640: exact path
  // redirects to `/` (HG1, query forwarded). Splat remains a marked
  // non-product design archive (not this cluster).
  // Index BEFORE its splat sibling as habit. Two entries only — the prototype
  // still resolves its own deeper pages so its page list does not read as
  // product IA here.
  styleguideVerificationRoute,
  styleguideVerificationSplatRoute,
  // WP11a / #46644 — HG8 `/classic` archive of the old product (WorkspaceHome
  // + VerifierHome). `/` remains the verification shell; do not unmount it.
  // Literal `/classic` BEFORE `/classic/verification`. Not bare-canvas — old
  // mode rail wraps these pages so the archive is visually the old product.
  classicHomeRoute,
  classicVerificationRoute,
  // #44577 — top-level singletons. WP11b / #46645: `/` is owned by the
  // verification product (HG1). Old mode rail/top nav is stripped via
  // bare-canvas exact `/`. `/styleguide/verification` is not canonical
  // (WP10a redirect). Literal `/help/settings` BEFORE any `/help/$…`
  // dynamic sibling so the static leaf wins (TanStack ranks by specificity;
  // registration order is habit for the Hono half).
  homeRoute,
  // HG12 / skeptic 0: NAV list pages are createRoute+VerificationShell in
  // singletons.tsx. `$id` children and `/workflows/new` stay in
  // verificationIaRoutes. Literals before `$id`. Prefix `/` is not used (HG7).
  inboxRoute,
  queueRoute,
  findingsRoute,
  runsRoute,
  workRoute,
  workflowsRoute,
  controlsRoute,
  policiesRoute,
  evidenceRoute,
  connectorsRoute,
  environmentsRoute,
  receiptsRoute,
  packsRoute,
  auditRoomRoute,
  workersRoute,
  scorecardRoute,
  runtimeRoute,
  sdkRoute,
  demoLabRoute,
  trustRoute,
  postureRoute,
  outcomesRoute,
  autonomyRoute,
  analyticsRoute,
  guideRoute,
  buyableRoute,
  designRoute,
  ...verificationIaRoutes,
  // WP10d / #46643 — searchable not-found recovery. Literal before any
  // later $param so /not-found is content, not a blank miss.
  verificationNotFoundRecoveryRoute,
  loginRoute,
  helpSettingsRoute,
  helpRoute,
  // WP10b / #46641 — settings/help/database utility aliases. Literals only;
  // do not steal canonical /settings /help /database or /help/settings.
  docsAliasRoute,
  documentationAliasRoute,
  helpCenterAliasRoute,
  dbAliasRoute,
  sqlAliasRoute,
  preferencesAliasRoute,
  profileAliasRoute,
  accountAliasRoute,
  settingsPersonalAliasRoute,
  settingsAppsAliasRoute,
  settingsAdvancedAliasRoute,
  settingsHelpAliasRoute,
  settingsDatabaseAliasRoute,
  devRoute,
  excellentNowRoute,
  notificationsRoute,
  integrationsConnectedRoute,
  onboardingWorkspaceRoute,
  // RO-3-11 — `/database`, all 16 pages. Literal paths BEFORE their dynamic
  // siblings (`objects/new` before `objects/$kind`, `schema` before
  // `schema/$object`) for the reason stated above: TanStack ranks by specificity
  // so it is habit here, but the Hono side is registration-ordered (#44370) and
  // one convention across both halves beats remembering which is which.
  databaseIndexRoute,
  databaseSchemaRoute,
  databaseSchemaObjectRoute,
  databaseMigrationsRoute,
  databaseSqlRoute,
  databaseSyncRoute,
  databaseImportRoute,
  databaseAssistantRoute,
  databaseEditorIndexRoute,
  databaseTableEditorRoute,
  databaseObjectsRoute,
  databaseObjectsNewRoute,
  databaseObjectTypeRoute,
  databaseConflictsRoute,
  databaseDuplicatesRoute,
  databaseViewsRoute,
  // RO-3-09 — settings section core. Redirect stubs first (index, legacy
  // leaves), then the real pages. Literal paths before any dynamic sibling
  // (settings connection/repo still PENDING in dynamic-segments). Honest
  // deferral of integrations / agents / advanced / multiplayer REWRITEs is
  // documented in the task evidence — same shape as RO-3-02's partial agents
  // port. SettingsPageShell is the one shell (packages/ui), used by every
  // real page below.
  settingsIndexRoute,
  settingsUserIndexRoute,
  settingsSectionsRoute,
  settingsReposRoute,
  settingsIntegrationsConnectionsRoute,
  settingsIntegrationsCatalogRoute,
  settingsCustomersIndexRoute,
  settingsAutoVerifyRoute,
  settingsWorkspaceIdentityRoute,
  settingsAgentsGoalsRoute,
  settingsAgentsWorkersRoute,
  settingsUserProfileRoute,
  settingsUserPreferencesRoute,
  settingsUserNotificationsRoute,
  settingsUserKeyboardRoute,
  settingsAppearanceRoute,
  settingsWorkspaceRoute,
  settingsWorkspaceSecurityRoute,
  settingsWorkspaceDevicesRoute,
  settingsCustomersPipelinesRoute,
  settingsCustomersLeadsRoute,
  settingsCustomersMessagingRoute,
  settingsCustomersSendingRoute,
  settingsCustomersDataRoute,
  // RO-10-08 — /settings/agents/models (Benchmark evidence + matrix). Required
  // by the R0 agents-surfaces suite; was missing and fell through to a default
  // settings redirect on the SPA host.
  settingsAgentsModelsRoute,
  // #44573 — remaining /settings/agents/* pages (repos + auto-verify first —
  // they are live redirect targets from settingsReposRoute / settingsAutoVerifyRoute).
  settingsAgentsReposRoute,
  settingsAgentsAutoVerifyRoute,
  settingsAgentsAuditsRoute,
  settingsAgentsAutopilotRoute,
  settingsAgentsClisRoute,
  settingsAgentsConsentRoute,
  settingsAgentsContextRoute,
  settingsAgentsCopilotRoute,
  settingsAgentsCredentialsRoute,
  settingsAgentsDatabaseRoute,
  settingsAgentsFleetRoute,
  settingsAgentsInboundRoute,
  settingsAgentsIntegrateRoute,
  settingsAgentsLearningRoute,
  settingsAgentsMirrorRoute,
  settingsAgentsResidencyRoute,
  settingsAgentsMonitorRoute,
  settingsAgentsNotifyRoute,
  settingsAgentsOptimizationRoute,
  settingsAgentsTelemetryRoute,
  settingsAgentsVerdictsRoute,
  // #44574 — settings advanced + integrations + workspace remainder + singletons.
  // Literals and more-specific dynamic segments before coarser siblings.
  settingsAdvancedDangerRoute,
  settingsAdvancedDataRoute,
  settingsAdvancedMcpRoute,
  settingsAdvancedPerformanceRoute,
  settingsAdvisorRoute,
  settingsCompanyProfileRoute,
  settingsPulseRoute,
  settingsIntegrationsSourcesNewRoute,
  settingsIntegrationsMappingRoute,
  settingsIntegrationsRecordsRoute,
  settingsIntegrationsActivityRoute,
  settingsIntegrationsConflictsRoute,
  settingsIntegrationsDatabaseRoute,
  settingsIntegrationsSettingsRoute,
  settingsIntegrationsHomeRoute,
  settingsWorkspaceArchiveRoute,
  settingsWorkspaceDataRoute,
  settingsWorkspaceDirectoryRoute,
  settingsWorkspaceDomainsRoute,
  settingsWorkspaceSectionsRoute,
  settingsWorkspaceTeamDevicesRoute,
  settingsWorkspaceTeamMembersRoute,
  settingsWorkspaceTeamPrimaryRoute,
  settingsWorkspaceTeamRolesRoute,
  settingsWorkspaceTeamRoute,
  // RO-3-03 — Work section. Literal paths BEFORE the `/work/$` splat so
  // `/work/o/$uid` wins over the catch-all (same precedence Next's file router
  // gave free, and the reason the original work/[...segments] comment says the
  // object route is more specific). Absorbed standalone roots (issues, planner,
  // activity, favorites, suggestions, verdicts) sit at the top level because
  // that is where their Next originals lived.
  workIndexRoute,
  workObjectRoute,
  workSectionsRoute,
  issuesRoute,
  plannerRoute,
  activityRoute,
  favoritesRoute,
  suggestionsRoute,
  overridesRoute,
  verdictsRoute,
  // RO-3-10 — projects + section-hosted views. Literal paths BEFORE their
  // dynamic siblings (`map` before `$projectSlug`, `leads/new` before `leads/$id`,
  // `jobs/new` before `jobs/$slug`, `sections/...` before `$projectSlug`).
  projectsIndexRoute,
  projectsMapRoute,
  projectsMapDefineRoute,
  projectTasksRedirectRoute,
  projectSettingsIdentityRoute,
  projectSettingsBriefRoute,
  projectSettingsTrackingRoute,
  projectSettingsOrganizationRoute,
  projectSettingsSectionsRoute,
  projectSettingsPreferencesRoute,
  workspaceSectionDashboardRoute,
  workspaceSectionAuditRoute,
  workspaceSectionIssuesRoute,
  workspaceSectionRoute,
  projectLeadsNewRoute,
  projectLeadsRoute,
  projectLeadDetailRoute,
  projectOpportunitiesNewRoute,
  projectOpportunitiesRoute,
  projectOpportunityDetailRoute,
  projectSequencesNewRoute,
  projectSequencesRoute,
  projectSequenceDetailRoute,
  projectTemplatesNewRoute,
  projectTemplatesRoute,
  projectReportsRoute,
  projectSectionSettingsRoute,
  projectJobsNewRoute,
  projectJobEditRoute,
  projectJobPipelineRoute,
  projectJobDetailRoute,
  projectJobsRoute,
  projectCandidatesRoute,
  projectCandidateDetailRoute,
  projectPipelineRoute,
  projectApplicationDetailRoute,
  // #44578 — goals/new BEFORE goals/$id so static `new` is not stolen as $id
  projectSectionGoalsNewRoute,
  projectGoalDetailRoute,
  projectSectionOverviewRoute,
  projectSectionAuditRoute,
  projectSectionImportRoute,
  projectSectionDirectoryRoute,
  projectSectionSimulateRoute,
  projectSectionChecklistRoute,
  projectSectionComponentsRoute,
  projectSectionTypographyRoute,
  projectSectionStyleGuideRoute,
  // #44578 — literal section leaves BEFORE the catch-all splat
  projectSectionAgentsRoute,
  projectSectionAnalyticsRoute,
  projectSectionIssuesRoute,
  projectSectionPerformanceRoute,
  projectSectionPlannerRoute,
  projectSectionPrdRoute,
  projectSectionSeoRoute,
  // #44578 — project-tier leaves BEFORE $sectionSlug (agents/issues/planner
  // must not be interpreted as section slugs)
  projectTierAgentsRoute,
  projectTierIssuesRoute,
  projectTierPlannerRoute,
  projectHomeRoute,
  projectSectionRoute,
  projectSectionCatchAllRoute,
  // RO-3-14 — remaining dynamic URL segments not yet claimed by other L3 ports.
  // `/welcome/$step`, the three `/database/**/$…` segments, and the Work section
  // paths above already live in their own files; this spread excludes those so
  // the tree never registers the same path twice. See `dynamic-segments.tsx`.
  //
  // Spread LAST: TanStack ranks by SPECIFICITY, not registration order, so the
  // literal routes above cannot be shadowed by a `$param` sibling here.
  ...dynamicSegmentRoutes,
]);

export const router = createRouter({ routeTree });

/**
 * Register the router's types globally so `useSearch()`, `<Link to>` and
 * `navigate()` are checked against THIS tree everywhere — including inside
 * `packages/ui` once RO-4-02 lands. Without this the typed-search guarantee
 * (I9) degrades to `unknown` at every call site outside this file.
 */
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
